import React from 'react';
import {
  Inbox,
  Search,
  FileX,
  FolderOpen,
  Database,
  AlertCircle,
  Plus,
  RefreshCw,
  Filter,
  Upload
} from 'lucide-react';
import { cn } from '../../lib/utils';
import Button from './Button';

export type EmptyStateType =
  | 'no-data'
  | 'no-results'
  | 'no-items'
  | 'error'
  | 'no-connection'
  | 'empty-folder'
  | 'no-filters';

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-data',
  title,
  message,
  icon,
  action,
  secondaryAction,
  className,
  size = 'md',
}) => {
  // Default content based on type
  const defaults: Record<EmptyStateType, { icon: React.ReactNode; title: string; message: string }> = {
    'no-data': {
      icon: <Database className="w-full h-full" />,
      title: 'No data yet',
      message: 'Get started by creating your first item or importing data.',
    },
    'no-results': {
      icon: <Search className="w-full h-full" />,
      title: 'No results found',
      message: 'Try adjusting your search or filter criteria to find what you\'re looking for.',
    },
    'no-items': {
      icon: <Inbox className="w-full h-full" />,
      title: 'Nothing here',
      message: 'This section is empty. Items will appear here once added.',
    },
    'error': {
      icon: <AlertCircle className="w-full h-full" />,
      title: 'Unable to load',
      message: 'We encountered a problem loading this content. Please try again.',
    },
    'no-connection': {
      icon: <FileX className="w-full h-full" />,
      title: 'Connection lost',
      message: 'Please check your internet connection and try again.',
    },
    'empty-folder': {
      icon: <FolderOpen className="w-full h-full" />,
      title: 'Empty folder',
      message: 'This folder doesn\'t contain any items yet.',
    },
    'no-filters': {
      icon: <Filter className="w-full h-full" />,
      title: 'No matching results',
      message: 'No items match your current filters. Try clearing some filters.',
    },
  };

  const config = defaults[type];
  const displayIcon = icon || config.icon;
  const displayTitle = title || config.title;
  const displayMessage = message || config.message;

  const sizeClasses = {
    sm: {
      container: 'py-8 px-4',
      iconWrapper: 'w-10 h-10',
      title: 'text-sm',
      message: 'text-xs',
    },
    md: {
      container: 'py-12 px-6',
      iconWrapper: 'w-14 h-14',
      title: 'text-base',
      message: 'text-sm',
    },
    lg: {
      container: 'py-16 px-8',
      iconWrapper: 'w-20 h-20',
      title: 'text-lg',
      message: 'text-base',
    },
  };

  const sizes = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizes.container,
        className
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex items-center justify-center rounded-2xl bg-slate-800/50 border border-white/5 text-slate-500 mb-4',
          sizes.iconWrapper,
          'p-3'
        )}
      >
        {displayIcon}
      </div>

      {/* Title */}
      <h3 className={cn('font-semibold text-white mb-1', sizes.title)}>
        {displayTitle}
      </h3>

      {/* Message */}
      <p className={cn('text-slate-400 max-w-sm mb-6', sizes.message)}>
        {displayMessage}
      </p>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {action && (
            <Button
              variant="primary"
              size={size === 'lg' ? 'md' : 'sm'}
              onClick={action.onClick}
              leftIcon={action.icon}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant="ghost"
              size={size === 'lg' ? 'md' : 'sm'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Preset Empty States for common scenarios
export const NoDataState: React.FC<{ onAdd?: () => void; itemName?: string }> = ({
  onAdd,
  itemName = 'item'
}) => (
  <EmptyState
    type="no-data"
    title={`No ${itemName}s yet`}
    message={`Get started by creating your first ${itemName}.`}
    action={onAdd ? {
      label: `Add ${itemName}`,
      onClick: onAdd,
      icon: <Plus className="w-4 h-4" />,
    } : undefined}
  />
);

export const NoSearchResultsState: React.FC<{
  searchTerm?: string;
  onClearSearch?: () => void;
}> = ({ searchTerm, onClearSearch }) => (
  <EmptyState
    type="no-results"
    title="No results found"
    message={searchTerm
      ? `No items match "${searchTerm}". Try a different search term.`
      : 'Try adjusting your search to find what you\'re looking for.'
    }
    action={onClearSearch ? {
      label: 'Clear search',
      onClick: onClearSearch,
      icon: <RefreshCw className="w-4 h-4" />,
    } : undefined}
  />
);

export const NoFilterResultsState: React.FC<{ onClearFilters?: () => void }> = ({
  onClearFilters
}) => (
  <EmptyState
    type="no-filters"
    action={onClearFilters ? {
      label: 'Clear all filters',
      onClick: onClearFilters,
      icon: <RefreshCw className="w-4 h-4" />,
    } : undefined}
  />
);

export const ErrorState: React.FC<{ onRetry?: () => void; error?: string }> = ({
  onRetry,
  error
}) => (
  <EmptyState
    type="error"
    message={error || 'We encountered a problem loading this content. Please try again.'}
    action={onRetry ? {
      label: 'Try again',
      onClick: onRetry,
      icon: <RefreshCw className="w-4 h-4" />,
    } : undefined}
  />
);

export const UploadState: React.FC<{ onUpload?: () => void; itemName?: string }> = ({
  onUpload,
  itemName = 'file'
}) => (
  <EmptyState
    type="empty-folder"
    icon={<Upload className="w-full h-full" />}
    title={`Upload ${itemName}s`}
    message={`Drag and drop ${itemName}s here, or click to browse.`}
    action={onUpload ? {
      label: `Upload ${itemName}`,
      onClick: onUpload,
      icon: <Upload className="w-4 h-4" />,
    } : undefined}
  />
);

// Table-specific empty state (inline, smaller)
export const TableEmptyState: React.FC<{
  message?: string;
  onAction?: () => void;
  actionLabel?: string;
}> = ({ message = 'No data available', onAction, actionLabel }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-10 h-10 rounded-xl bg-slate-800/50 border border-white/5 flex items-center justify-center text-slate-500 mb-3">
      <Inbox className="w-5 h-5" />
    </div>
    <p className="text-sm text-slate-400 mb-3">{message}</p>
    {onAction && actionLabel && (
      <button
        onClick={onAction}
        className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export default EmptyState;
